import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  MapPin, Ruler, BedDouble, Bath, Home, TreePalm, Car,
  Thermometer, Calendar, Flame, Shield, Sun, Building2,
  Phone, Mail, User, Save, XCircle, Minus, Plus, Trees,
  Warehouse, Router
} from 'lucide-react';
import { loadingSteps } from '../lib/constants';
import RangeSlider from './RangeSlider';
import type { FormData } from '../types';
import type { EstimationFormProps } from './estimation/types';

const stepTitles = [
  'Caractéristiques principales',
  'Terrain et construction',
  'État et équipements',
  'Extérieur et dépendances',
  'Vos coordonnées'
];

const propertyTypes = [
  { value: 'individuelle', label: 'Maison individuelle' },
  { value: 'mitoyenne', label: 'Maison mitoyenne' },
  { value: 'copropriete', label: 'En copropriété' }
];

const roofTypes = [
  { value: 'tuiles', label: 'Tuiles' },
  { value: 'ardoises', label: 'Ardoises' },
  { value: 'zinc', label: 'Zinc' },
  { value: 'terrasse', label: 'Toit terrasse' }
];

const insulationTypes = [
  { value: 'thermique', label: 'Thermique' },
  { value: 'phonique', label: 'Phonique' },
  { value: 'double', label: 'Double isolation' },
  { value: 'aucune', label: 'Aucune isolation' }
];

const energyEquipments = [
  { value: 'pompe_chaleur', label: 'Pompe à chaleur' },
  { value: 'panneaux_solaires', label: 'Panneaux solaires' },
  { value: 'chauffe_eau_solaire', label: 'Chauffe-eau solaire' },
  { value: 'recuperateur_eau', label: 'Récupérateur d\'eau' },
  { value: 'borne_recharge', label: 'Borne de recharge électrique' }
];

const accessTypes = [
  { value: 'independant', label: 'Accès indépendant' },
  { value: 'partage', label: 'Accès partagé' }
];

interface HouseEstimationFormProps extends EstimationFormProps {
  onBackToPropertyType?: () => void;
}

function HouseEstimationForm({
  initialAddress = '',
  visible = false,
  onClose,
  onBackToPropertyType
}: HouseEstimationFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [coordinates, setCoordinates] = useState<{longitude?: number; latitude?: number}>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<FormData>({
    civilite: 'M',
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    adresse: initialAddress,
    type_propriete: 'Maison',
    type_maison: '',
    surface_terrain: 0,
    surface: 0,
    nb_pieces: 0,
    nb_chambres: 0,
    nbr_salle_de_bain: 0,
    nbre_etages: 1,
    annee_construction: '',
    type_toiture: '',
    type_isolation: '',
    acces: 'independant',
    cave: false,
    garage: false,
    abri_jardin: false,
    equipements_energetiques: [],
    jardin: null,
    terrasse: null,
    nature_chauffage: 'unknown',
    type_chauffage: 'unknown',
    etat_general: '50',
    etat_toiture: '50',
    etat_facade: '50',
    etat_menuiseries: '50',
    etat_sols: '50',
    exposition: 'unknown',
    proprietaire: 'owner',
    created_at: new Date().toISOString()
  });

  useEffect(() => {
    if (initialAddress) {
      setFormData(prev => ({ ...prev, adresse: initialAddress }));
      fetchCoordinates(initialAddress);
    }
  }, [initialAddress]);

  const fetchCoordinates = async (address: string) => {
    try {
      const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(address)}&limit=1`);
      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const [longitude, latitude] = data.features[0].geometry.coordinates;
        setCoordinates({ longitude, latitude });
      }
    } catch (error) {
      console.error('Error fetching coordinates:', error);
    }
  };

  const validateStep1 = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.type_maison) {
      newErrors.type_maison = 'Le type de maison est requis';
    }
    if (formData.surface_terrain <= 0) {
      newErrors.surface_terrain = 'La surface du terrain doit être supérieure à 0';
    }
    if (formData.surface <= 0) {
      newErrors.surface = 'La surface bâtie doit être supérieure à 0';
    }
    if (formData.nb_pieces <= 0) {
      newErrors.nb_pieces = 'Le nombre de pièces doit être supérieur à 0';
    }
    if (formData.nb_chambres <= 0) {
      newErrors.nb_chambres = 'Le nombre de chambres doit être supérieur à 0';
    }
    if (formData.nb_chambres > formData.nb_pieces) {
      newErrors.nb_chambres = 'Le nombre de chambres ne peut pas être supérieur au nombre de pièces';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleNumberChange = (field: keyof FormData, increment: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: increment ? (prev[field] as number) + 1 : Math.max(0, (prev[field] as number) - 1)
    }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSliderChange = (field: keyof FormData) => (value: number) => {
    setFormData(prev => ({ ...prev, [field]: value.toString() }));
  };

  const runLoadingAnimation = async () => {
    setIsSubmitting(true);
    for (let i = 0; i < loadingSteps.length; i++) {
      setLoadingStep(i);
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await runLoadingAnimation();

      fetch('/api/submit-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, ...coordinates })
      }).catch(error => {
        console.error('Error in background submission:', error);
      });

      toast.success('Votre demande d\'estimation a été envoyée avec succès !');

      if (onClose) {
        onClose();
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Une erreur est survenue lors de l\'envoi de votre demande');
    } finally {
      setIsSubmitting(false);
      setLoadingStep(0);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-8">
            <h3 className="text-lg font-medium text-gray-900">
              Caractéristiques principales
            </h3>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type de maison*
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {propertyTypes.map(({ value, label }) => (
                    <label
                      key={value}
                      className={`
                        flex items-center p-4 rounded-lg cursor-pointer border-2
                        transition-all duration-300 hover:scale-[1.02]
                        ${formData.type_maison === value
                          ? 'bg-gradient-to-r from-[#2A4365]/10 via-[#4A5568]/10 to-[#C4A052]/10 border-[#2A4365]'
                          : 'bg-white border-gray-200 hover:bg-gradient-to-r hover:from-[#2A4365]/5 hover:via-[#4A5568]/5 hover:to-[#C4A052]/5'}
                      `}
                    >
                      <input
                        type="radio"
                        name="type_maison"
                        value={value}
                        checked={formData.type_maison === value}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <Home
                        className={`h-6 w-6 mr-3 transition-colors ${
                          formData.type_maison === value
                            ? 'text-[#2A4365]'
                            : 'text-gray-400'
                        }`}
                      />
                      <span className={`${
                        formData.type_maison === value
                          ? 'text-[#2A4365] font-medium'
                          : 'text-gray-700'
                      }`}>{label}</span>
                    </label>
                  ))}
                </div>
                {errors.type_maison && (
                  <p className="mt-1 text-sm text-red-600">{errors.type_maison}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Surface du terrain (m²)*
                </label>
                <div className="relative">
                  <Trees className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="number"
                    name="surface_terrain"
                    value={formData.surface_terrain || ''}
                    onChange={handleInputChange}
                    className={`pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                      errors.surface_terrain ? 'border-red-500' : ''
                    }`}
                    required
                    min="1"
                  />
                </div>
                {errors.surface_terrain && (
                  <p className="mt-1 text-sm text-red-600">{errors.surface_terrain}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Surface habitable (m²)*
                </label>
                <div className="relative">
                  <Ruler className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="number"
                    name="surface"
                    value={formData.surface || ''}
                    onChange={handleInputChange}
                    className={`pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                      errors.surface ? 'border-red-500' : ''
                    }`}
                    required
                    min="1"
                  />
                </div>
                {errors.surface && (
                  <p className="mt-1 text-sm text-red-600">{errors.surface}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de pièces*
                  </label>
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => handleNumberChange('nb_pieces', false)}
                      className="p-2 border border-gray-300 rounded-l-lg hover:bg-gray-50"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <input
                      type="number"
                      name="nb_pieces"
                      value={formData.nb_pieces || ''}
                      onChange={handleInputChange}
                      className={`w-16 text-center border-y border-gray-300 ${
                        errors.nb_pieces ? 'border-red-500' : ''
                      }`}
                      min="1"
                    />
                    <button
                      type="button"
                      onClick={() => handleNumberChange('nb_pieces', true)}
                      className="p-2 border border-gray-300 rounded-r-lg hover:bg-gray-50"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  {errors.nb_pieces && (
                    <p className="mt-1 text-sm text-red-600">{errors.nb_pieces}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de chambres*
                  </label>
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => handleNumberChange('nb_chambres', false)}
                      className="p-2 border border-gray-300 rounded-l-lg hover:bg-gray-50"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <input
                      type="number"
                      name="nb_chambres"
                      value={formData.nb_chambres || ''}
                      onChange={handleInputChange}
                      className={`w-16 text-center border-y border-gray-300 ${
                        errors.nb_chambres ? 'border-red-500' : ''
                      }`}
                      min="1"
                    />
                    <button
                      type="button"
                      onClick={() => handleNumberChange('nb_chambres', true)}
                      className="p-2 border border-gray-300 rounded-r-lg hover:bg-gray-50"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  {errors.nb_chambres && (
                    <p className="mt-1 text-sm text-red-600">{errors.nb_chambres}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de salles de bain*
                  </label>
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => handleNumberChange('nbr_salle_de_bain', false)}
                      className="p-2 border border-gray-300 rounded-l-lg hover:bg-gray-50"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <input
                      type="number"
                      name="nbr_salle_de_bain"
                      value={formData.nbr_salle_de_bain || ''}
                      onChange={handleInputChange}
                      className="w-16 text-center border-y border-gray-300"
                      min="1"
                    />
                    <button
                      type="button"
                      onClick={() => handleNumberChange('nbr_salle_de_bain', true)}
                      className="p-2 border border-gray-300 rounded-r-lg hover:bg-gray-50"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8">
            <h3 className="text-lg font-medium text-gray-900">
              Terrain et construction
            </h3>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Année de construction*
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="number"
                    name="annee_construction"
                    value={formData.annee_construction || ''}
                    onChange={handleInputChange}
                    className="pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    min="1800"
                    max={new Date().getFullYear()}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type de toiture
                </label>
                <select
                  name="type_toiture"
                  value={formData.type_toiture}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="">Sélectionnez le type de toiture</option>
                  {roofTypes.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type d'isolation
                </label>
                <select
                  name="type_isolation"
                  value={formData.type_isolation}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="">Sélectionnez le type d'isolation</option>
                  {insulationTypes.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de niveaux
                </label>
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => handleNumberChange('nbre_etages', false)}
                    className="p-2 border border-gray-300 rounded-l-lg hover:bg-gray-50"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    name="nbre_etages"
                    value={formData.nbre_etages || 1}
                    onChange={handleInputChange}
                    className="w-16 text-center border-y border-gray-300"
                    min="1"
                  />
                  <button
                    type="button"
                    onClick={() => handleNumberChange('nbre_etages', true)}
                    className="p-2 border border-gray-300 rounded-r-lg hover:bg-gray-50"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type d'accès
                </label>
                <select
                  name="acces"
                  value={formData.acces}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  {accessTypes.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8">
            <h3 className="text-lg font-medium text-gray-900">
              État et équipements
            </h3>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  État général
                </label>
                <RangeSlider
                  value={parseInt(formData.etat_general)}
                  onChange={handleSliderChange('etat_general')}
                  labels={{
                    0: 'Mauvais',
                    50: 'Bon',
                    100: 'Excellent'
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  État de la toiture
                </label>
                <RangeSlider
                  value={parseInt(formData.etat_toiture)}
                  onChange={handleSliderChange('etat_toiture')}
                  labels={{
                    0: 'Mauvais',
                    50: 'Bon',
                    100: 'Excellent'
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  État de la façade
                </label>
                <RangeSlider
                  value={parseInt(formData.etat_facade)}
                  onChange={handleSliderChange('etat_facade')}
                  labels={{
                    0: 'Mauvais',
                    50: 'Bon',
                    100: 'Excellent'
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  État des menuiseries
                </label>
                <RangeSlider
                  value={parseInt(formData.etat_menuiseries)}
                  onChange={handleSliderChange('etat_menuiseries')}
                  labels={{
                    0: 'Mauvais',
                    50: 'Bon',
                    100: 'Excellent'
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Équipements énergétiques
                </label>
                <div className="space-y-2">
                  {energyEquipments.map(({ value, label }) => (
                    <label key={value} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.equipements_energetiques.includes(value)}
                        onChange={(e) => {
                          const newEquipements = e.target.checked
                            ? [...formData.equipements_energetiques, value]
                            : formData.equipements_energetiques.filter(v => v !== value);
                          setFormData({ ...formData, equipements_energetiques: newEquipements });
                        }}
                        className="w-5 h-5 rounded border-gray-300
                          checked:bg-gradient-to-r checked:from-[#2A4365] checked:via-[#4A5568] checked:to-[#C4A052]
                          focus:ring-[#2A4365] focus:ring-offset-2 transition-all duration-300"
                      />
                      <span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nature du chauffage
                </label>
                <select
                  name="nature_chauffage"
                  value={formData.nature_chauffage}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                >
                  <option value="unknown">Choisir la nature du chauffage</option>
                  <option value="electrique">Électrique</option>
                  <option value="gaz">Gaz</option>
                  <option value="fioul">Fioul</option>
                  <option value="bois">Bois</option>
                  <option value="pompe_chaleur">Pompe à chaleur</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-8">
            <h3 className="text-lg font-medium text-gray-900">
              Extérieur et dépendances
            </h3>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Exposition
                </label>
                <select
                  name="exposition"
                  value={formData.exposition}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                >
                  <option value="unknown">Choisir l'exposition</option>
                  <option value="nord">Nord</option>
                  <option value="sud">Sud</option>
                  <option value="est">Est</option>
                  <option value="ouest">Ouest</option>
                  <option value="nord-est">Nord-Est</option>
                  <option value="nord-ouest">Nord-Ouest</option>
                  <option value="sud-est">Sud-Est</option>
                  <option value="sud-ouest">Sud-Ouest</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Jardin (m²)
                </label>
                <div className="relative">
                  <TreePalm className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="number"
                    name="jardin"
                    value={formData.jardin || ''}
                    onChange={handleInputChange}
                    className="pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Surface en m²"
                    min="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Terrasse (m²)
                </label>
                <div className="relative">
                  <Home className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="number"
                    name="terrasse"
                    value={formData.terrasse || ''}
                    onChange={handleInputChange}
                    className="pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Surface en m²"
                    min="0"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="garage"
                    checked={formData.garage}
                    onChange={handleInputChange}
                    className="w-5 h-5 rounded border-gray-300
                      checked:bg-gradient-to-r checked:from-[#2A4365] checked:via-[#4A5568] checked:to-[#C4A052]
                      focus:ring-[#2A4365] focus:ring-offset-2 transition-all duration-300"
                  />
                  <span className="text-sm text-gray-700">Garage</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="cave"
                    checked={formData.cave}
                    onChange={handleInputChange}
                    className="w-5 h-5 rounded border-gray-300
                      checked:bg-gradient-to-r checked:from-[#2A4365] checked:via-[#4A5568] checked:to-[#C4A052]
                      focus:ring-[#2A4365] focus:ring-offset-2 transition-all duration-300"
                  />
                  <span className="text-sm text-gray-700">Cave</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="abri_jardin"
                    checked={formData.abri_jardin}
                    onChange={handleInputChange}
                    className="w-5 h-5 rounded border-gray-300
                      checked:bg-gradient-to-r checked:from-[#2A4365] checked:via-[#4A5568] checked:to-[#C4A052]
                      focus:ring-[#2A4365] focus:ring-offset-2 transition-all duration-300"
                  />
                  <span className="text-sm text-gray-700">Abri de jardin</span>
                </label>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Civilité*
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="civilite"
                      value="M"
                      checked={formData.civilite === 'M'}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded-full border-gray-300
                        checked:bg-gradient-to-r checked:from-[#2A4365] checked:via-[#4A5568] checked:to-[#C4A052]
                        focus:ring-[#2A4365] focus:ring-offset-2 transition-all duration-300"
                    />
                    <span className="ml-2">M.</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="civilite"
                      value="Mme"
                      checked={formData.civilite === 'Mme'}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded-full border-gray-300
                        checked:bg-gradient-to-r checked:from-[#2A4365] checked:via-[#4A5568] checked:to-[#C4A052]
                        focus:ring-[#2A4365] focus:ring-offset-2 transition-all duration-300"
                    />
                    <span className="ml-2">Mme</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prénom
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleInputChange}
                    className="pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                    className="pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="tel"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleInputChange}
                    className="pl-10 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`max-w-4xl mx-auto px-4 py-12 ${visible ? 'animate-fade-in' : ''}`}>
      {isSubmitting && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 text-center">
            <div className="relative w-40 h-40 mx-auto mb-6">
              <div className="absolute inset-0 border-4 border-gray-200 rounded-full" />
              <div
                className="absolute inset-0 border-4 border-transparent border-t-[#2A4365] rounded-full animate-spin"
                style={{
                  background: 'linear-gradient(to right, transparent, transparent 50%, #4A5568 50%, #C4A052)',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude'
                }}
              />
            </div>
            <p className="text-2xl font-semibold text-gray-900 mb-2">
              {loadingSteps[loadingStep]}
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div
                className="h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${((loadingStep + 1) / loadingSteps.length) * 100}%`,
                  background: 'linear-gradient(to right, #2A4365, #4A5568, #C4A052)'
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-xl p-8">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
          {currentStep}/5 {stepTitles[currentStep - 1]}
        </h2>

        <div className="mb-8">
          <div className="relative h-3">
            <div className="absolute inset-0 bg-gray-200 rounded-full" />

            <div
              className="absolute inset-0 rounded-full transition-all duration-300"
              style={{
                width: `${(currentStep / 5) * 100}%`,
                background: 'linear-gradient(to right, #2A4365, #4A5568, #C4A052)'
              }}
            />

            <div className="absolute inset-0 flex justify-between items-center px-1">
              {Array.from({ length: 5 }, (_, i) => (
                <div
                  key={i}
                  className={`w-5 h-5 rounded-full border-2 transition-all duration-300`}
                  style={{
                    background: i + 1 <= currentStep
                      ? 'linear-gradient(to right, #2A4365, #4A5568, #C4A052)'
                      : '#E5E7EB',
                    borderColor: i + 1 <= currentStep ? '#2A4365' : '#9CA3AF'
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {initialAddress && (
          <div className="bg-white/80 backdrop-blur-xl p-8 rounded-xl shadow-sm mb-8">
            <div className="flex items-center justify-center space-x-4">
              <div className="bg-gradient-to-r from-[#2A4365]/10 via-[#4A5568]/10 to-[#C4A052]/10 p-3 rounded-full">
                <MapPin className="h-10 w-10 text-[#2A4365]" strokeWidth={2} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{initialAddress}</h3>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {renderStepContent()}

          <div className="flex justify-between pt-6">
            {currentStep > 1 ? (
              <button
                type="button"
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all duration-300"
              >
                Précédent
              </button>
            ) : (
              <button
                type="button"
                onClick={onBackToPropertyType}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all duration-300"
              >
                Retour au type de bien
              </button>
            )}
            {currentStep < 5 ? (
              <button
                type="button"
                onClick={() => {
                  if (currentStep === 1) {
                    if (validateStep1()) {
                      setCurrentStep(prev => prev + 1);
                    }
                  } else {
                    setCurrentStep(prev => prev + 1);
                  }
                }}
                className="ml-auto px-6 py-2 text-white rounded-lg transition-all duration-300 hover:scale-105 bg-gradient-to-r from-[#2A4365] via-[#4A5568] to-[#C4A052] hover:from-[#1A3355] hover:via-[#3A4558] hover:to-[#B49042]"
              >
                Suivant
              </button>
            ) : (
              <button
                type="submit"
                className="ml-auto px-6 py-2 text-white rounded-lg transition-all duration-300 hover:scale-105 bg-gradient-to-r from-[#2A4365] via-[#4A5568] to-[#C4A052] hover:from-[#1A3355] hover:via-[#3A4558] hover:to-[#B49042]"
              >
                Obtenir l'estimation
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default HouseEstimationForm;